import { createFieldErrorId, createId } from "@edgetier/utilities";
import classNames from "classnames";
import { Field, FieldProps } from "formik";

import { useRef } from "react";
import SelectOptions from "../select-options";
import { IProps } from "./buttons-form-field.types";

/**
 * Buttons field type for dynamic forms. This behaves the same as a set of radio buttons, the only difference is how
 * they are displayed. It uses radio buttons behind the scenes for accessibility.
 * @param props.autoFocus  Automatically focus on the field when it renders.
 * @param props.formField  Buttons form field.
 * @param props.languageId User's language.
 * @returns                Multiple buttons.
 */
const Buttons = ({ autoFocus, formField, languageId, translation }: IProps) => {
    const containerRef = useRef<HTMLDivElement>(null);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (!containerRef.current) return;

        const radioButtons = containerRef.current.querySelectorAll(
            'input[type="radio"]'
        ) as NodeListOf<HTMLInputElement>;
        const currentIndex = Array.from(radioButtons).findIndex((radio) => radio === document.activeElement);

        if (currentIndex === -1) return;

        const isFirstButton = currentIndex === 0;
        const isLastButton = currentIndex === radioButtons.length - 1;

        // If the nextIndex is the last button, go to the first button
        const nextIndex = isLastButton ? 0 : (currentIndex + 1) % radioButtons.length;

        // If the previousIndex is the first button, go to the last button
        const previousIndex = isFirstButton
            ? radioButtons.length - 1
            : (currentIndex - 1 + radioButtons.length) % radioButtons.length;

        switch (event.key) {
            case "ArrowDown":
            case "ArrowRight":
                event.preventDefault();
                radioButtons[nextIndex].focus();
                break;

            case "ArrowUp":
            case "ArrowLeft":
                event.preventDefault();
                radioButtons[previousIndex].focus();
                break;

            case "Enter":
                radioButtons[currentIndex].click();
                break;

            default:
                // Don't prevent default for unhandled keys
                event.preventDefault = () => {};
                return;
        }
    };

    return (
        <Field name={formField.fieldName}>
            {({ field: { name, value }, form: { errors, setFieldValue } }: FieldProps) => (
                <div
                    ref={containerRef}
                    aria-label={translation.formFieldTranslation}
                    aria-required={formField.required}
                    className={classNames("dynamic-form__buttons", {
                        "dynamic-form__buttons--dirty": value.length > 0,
                    })}
                    role="radiogroup"
                    onKeyDown={handleKeyDown}
                >
                    <div className="dynamic-form__buttons__container">
                        <SelectOptions languageId={languageId} selectOptions={formField.configuration.selectOptions}>
                            {({ index, label, selectOptionId }) => (
                                <div
                                    className={classNames("dynamic-form__buttons__button", {
                                        "dynamic-form__buttons__button--not-selected": value[0] !== selectOptionId,
                                        "dynamic-form__buttons__button--selected": value[0] === selectOptionId,
                                    })}
                                    key={selectOptionId}
                                >
                                    <input
                                        aria-describedby={createFieldErrorId(name)}
                                        autoFocus={autoFocus && index === 0}
                                        checked={selectOptionId === value[0]}
                                        id={createId(name, selectOptionId)}
                                        name={name}
                                        onChange={(changeEvent) =>
                                            changeEvent.target.checked && setFieldValue(name, [selectOptionId])
                                        }
                                        type="radio"
                                    />
                                    <label htmlFor={createId(name, selectOptionId)}>{label}</label>
                                </div>
                            )}
                        </SelectOptions>
                    </div>
                </div>
            )}
        </Field>
    );
};

export default Buttons;
